/**
 * Pure data derivation for the "I2V" before/after showcase (no React / UI deps,
 * unit-tested).
 *
 * The section shows, per animated (image-to-video) scene, the source photo that
 * was fed to the i2v provider (the "before"), the generated clip it became (the
 * "after"), and the effective prompt/model behind it. All three already reach
 * the CRM; this module just joins them:
 *
 *   - the "after" clip is a MEDIA row with `asset_role === "i2v"` (kind `i2v`),
 *     carrying `scene_id` + `i2v_prompt_audit_summary` (see providers/aws mapMedia);
 *   - the "before" photo + planned prompt come from `buildStoryboard` (matched to
 *     the scene by `video_prompt.file` basename);
 *   - the effective prompt text comes from `sceneI2VByScene` (the durable
 *     `echo-state.v1.json` manifest / journey, resolved in EchoflicksSessionShow).
 *
 * The build is OUTPUT-DRIVEN: we iterate the i2v output rows (which always carry
 * `scene_id`) and enrich each from the storyboard scene + manifest audit. That is
 * more robust than script-driven iteration for older scripts whose scenes lack ids.
 */

import type { EchoflicksSessionMedia } from "../types";
import { buildStoryboard, type SceneI2VAudit } from "./scriptStory";
import { isVideo } from "./sessionUtils";

export type I2VPair = {
  /** Stable React key. */
  key: string;
  sceneId: string | number | null;
  /** Display order: script scene index when known, else null (fallbacks below). */
  index: number | null;
  /** The source photo fed into i2v ("before"); undefined when unmatched. */
  sourceMedia?: EchoflicksSessionMedia;
  /** Basename of the source file, for a placeholder label when unmatched. */
  sourceFile?: string | null;
  /** The generated i2v clip ("after") — always present (pairs are output-driven). */
  outputMedia: EchoflicksSessionMedia;
  /** Effective prompt/model audit (manifest wins, journey fills gaps). */
  audit?: SceneI2VAudit | null;
  /**
   * The uploaded image that filled the i2v FIRST-frame slot, resolved to a media
   * row so it can be signed + shown. Usually the same as `sourceMedia`; may differ
   * for in-camera transition seams (a harvested previous-clip frame).
   */
  firstFrameMedia?: EchoflicksSessionMedia;
  /**
   * The uploaded image that filled the i2v END-frame slot (match-cut target photo,
   * or the looped-back source), resolved to a media row. Undefined for the common
   * single-image case (no end frame).
   */
  endFrameMedia?: EchoflicksSessionMedia;
  durationSeconds?: number | null;
  sceneType?: string | null;
};

/** Index the media rows by source-file basename (same keying as buildStoryboard). */
function mediaByFileName(
  media: EchoflicksSessionMedia[],
): Map<string, EchoflicksSessionMedia> {
  const byName = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const name =
      item.s3_key?.split("/").at(-1) ?? item.local_path?.split(/[\\/]/).at(-1);
    if (name && !byName.has(name)) byName.set(name, item);
  }
  return byName;
}

function normalizeStorageKey(value: string): string {
  return value.replaceAll("\\", "/").replace(/^\/+/, "");
}

function mediaByStorageKey(
  media: EchoflicksSessionMedia[],
): Map<string, EchoflicksSessionMedia> {
  const byKey = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const key = item.s3_key ?? item.storage_path;
    if (key) byKey.set(normalizeStorageKey(key), item);
  }
  return byKey;
}

/** Resolve a reference-frame file name to its uploaded media row, if present. */
function resolveReferenceMedia(
  fileName: string | null | undefined,
  storageKey: string | null | undefined,
  byStorageKey: Map<string, EchoflicksSessionMedia>,
  byName: Map<string, EchoflicksSessionMedia>,
): EchoflicksSessionMedia | undefined {
  if (storageKey) {
    return byStorageKey.get(normalizeStorageKey(storageKey));
  }
  if (!fileName) return undefined;
  const base = fileName.split(/[\\/]/).at(-1) ?? fileName;
  return byName.get(base);
}

/** True when a media row is (or can be inferred as) an i2v output clip. */
export function isI2VOutputMedia(media: EchoflicksSessionMedia): boolean {
  if (media.asset_role !== "i2v" && media.kind !== "i2v") return false;
  // `mime_type` is often absent on i2v rows; `mapMedia` infers video/mp4 from the
  // `.mp4` key, but guard directly on the extension too (gotcha #2).
  return (
    isVideo(media.mime_type) ||
    Boolean(media.s3_key?.toLowerCase().endsWith(".mp4"))
  );
}

function sceneKeyOf(value: string | number | null | undefined): string | null {
  if (value == null) return null;
  const str = String(value);
  return str.trim() ? str : null;
}

/**
 * Among several output rows for the same scene (retries), prefer the accepted
 * attempt, then the most recently created. Returns true when `next` should
 * replace `prev`.
 */
function preferOutput(
  next: EchoflicksSessionMedia,
  prev: EchoflicksSessionMedia,
): boolean {
  const nextAccepted =
    next.i2v_prompt_audit_summary?.asset_disposition === "accepted";
  const prevAccepted =
    prev.i2v_prompt_audit_summary?.asset_disposition === "accepted";
  if (nextAccepted !== prevAccepted) return nextAccepted;
  return (
    String(next.created_at_source ?? "") >= String(prev.created_at_source ?? "")
  );
}

/**
 * Build the ordered list of before/after pairs. Returns `[]` (→ the section
 * hides) when there are no i2v output rows.
 */
export function buildI2VPairs(
  envelope: Record<string, unknown> | undefined,
  media: EchoflicksSessionMedia[],
  sceneI2VByScene?: Map<string | number, SceneI2VAudit> | null,
): I2VPair[] {
  // 1. Collect + dedup the i2v output clips by scene_id (accepted > latest).
  const outputs = media.filter(isI2VOutputMedia);
  if (outputs.length === 0) return [];

  const bySceneOutput = new Map<string, EchoflicksSessionMedia>();
  const unkeyed: EchoflicksSessionMedia[] = [];
  for (const row of outputs) {
    const key = sceneKeyOf(row.scene_id);
    if (!key) {
      unkeyed.push(row);
      continue;
    }
    const prev = bySceneOutput.get(key);
    if (!prev || preferOutput(row, prev)) bySceneOutput.set(key, row);
  }

  // 2. Storyboard scenes give us the source photo, duration, type, and (already
  //    joined) the manifest/journey audit per scene, keyed by scene_id.
  const scenes = buildStoryboard(envelope, media, sceneI2VByScene);
  const sceneByKey = new Map<string, (typeof scenes)[number]>();
  scenes.forEach((scene, index) => {
    const key = sceneKeyOf(scene.sceneId) ?? `index:${index}`;
    if (!sceneByKey.has(key)) sceneByKey.set(key, scene);
  });

  const byName = mediaByFileName(media);
  const byStorageKey = mediaByStorageKey(media);
  const pairs: I2VPair[] = [];

  for (const [sceneKey, outputMedia] of bySceneOutput) {
    const scene = sceneByKey.get(sceneKey);
    const audit =
      scene?.i2v ??
      sceneI2VByScene?.get(sceneKey) ??
      sceneI2VByScene?.get(Number(sceneKey)) ??
      null;
    // Resolve the actual i2v INPUT reference frames (first + optional end frame)
    // to media rows so the CRM shows the uploaded references, not sampled output
    // frames. Falls back to the storyboard source photo for the first frame.
    const refFrames = audit?.referenceFrames ?? null;
    const resolvedFirstFrameMedia = resolveReferenceMedia(
      refFrames?.firstFrame?.fileName,
      refFrames?.firstFrame?.storageKey,
      byStorageKey,
      byName,
    );
    const firstFrameMedia =
      resolvedFirstFrameMedia ??
      (refFrames?.firstFrame?.storageKey ? undefined : scene?.media);
    const sourceMedia = firstFrameMedia;
    const endFrameMedia = resolveReferenceMedia(
      refFrames?.endFrame?.fileName,
      refFrames?.endFrame?.storageKey,
      byStorageKey,
      byName,
    );
    pairs.push({
      key: `i2v-${sceneKey}`,
      sceneId: scene?.sceneId ?? outputMedia.scene_id ?? sceneKey,
      index: scene?.index ?? null,
      sourceMedia,
      sourceFile: sourceFileOf(sourceMedia),
      outputMedia,
      audit,
      firstFrameMedia,
      endFrameMedia,
      durationSeconds: scene?.durationSeconds ?? null,
      sceneType: scene?.type ?? audit?.finalSceneType ?? null,
    });
  }

  // Output rows with no scene_id (rare): render output-only, no source join.
  unkeyed.forEach((outputMedia, i) => {
    pairs.push({
      key: `i2v-unkeyed-${outputMedia.id ?? i}`,
      sceneId: null,
      index: null,
      outputMedia,
      audit: null,
      durationSeconds: null,
      sceneType: null,
    });
  });

  // 3. Order by script index, then numeric scene_id, then created_at.
  pairs.sort((a, b) => {
    const ai = a.index ?? Number.POSITIVE_INFINITY;
    const bi = b.index ?? Number.POSITIVE_INFINITY;
    if (ai !== bi) return ai - bi;
    const an = Number(a.sceneId);
    const bn = Number(b.sceneId);
    if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
    return String(a.outputMedia.created_at_source ?? "").localeCompare(
      String(b.outputMedia.created_at_source ?? ""),
    );
  });

  return pairs;
}

function sourceFileOf(media?: EchoflicksSessionMedia): string | null {
  if (!media) return null;
  return (
    media.s3_key?.split("/").at(-1) ??
    media.local_path?.split(/[\\/]/).at(-1) ??
    null
  );
}
